import type { Ref } from 'vue';
import type { Entitlement, MonospacePolicyEntitlementResource, PolicyEntitlementAction } from '#layers/access/types/permissions/policy';
import type { GlobalCollectionId } from '#layers/data-model/public';
import { decodeEntitlementKey, encodeEntitlementKey, entitlementKeyMatchesAction, removeEntitlementKey } from '#layers/access/utils/entitlement-codec';

interface UsePolicyEntitlementsOptions {
	entitlements: Ref<Entitlement[]>;
}

export function usePolicyEntitlements({ entitlements }: UsePolicyEntitlementsOptions) {
	return {
		addEntitlementWildcard,
		addResourceSelection,
		setEntitlements,
		removeEntitlement,
		hasWildcardEntitlement,
		enabledActionsForResource,
		selectedResourceIdsForEntitlement,
		hasEntitlement,
		removeResourceSelection,
	};

	function addEntitlementWildcard(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		const newEntitlements = filterEntitlements(resource, action);
		newEntitlements.push(encodeEntitlementKey({ resource: resource.name, action: action.name, resourceId: action.hasIdentifier ? '*' : undefined }));
		entitlements.value = newEntitlements;
	}

	function setEntitlements(changedEntitlements: Entitlement[], resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		const newEntitlements = filterEntitlements(resource, action);
		newEntitlements.push(...changedEntitlements);
		entitlements.value = newEntitlements;
	}

	function removeEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		entitlements.value = filterEntitlements(resource, action);
	}

	function removeResourceSelection(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId: GlobalCollectionId) {
		entitlements.value = filterEntitlements(resource, action, resourceId);
	}

	function hasWildcardEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		return entitlements.value?.includes(encodeEntitlementKey({ resource: resource.name, action: action.name, resourceId: '*' }));
	}

	function hasEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction) {
		if (hasWildcardEntitlement(resource, action)) {
			return true;
		}

		return entitlements.value?.some((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name })) ?? false;
	}

	function enabledActionsForResource(resource: MonospacePolicyEntitlementResource) {
		return resource.actions.filter((action) => hasEntitlement(resource, action));
	}

	function selectedResourceIdsForEntitlement(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction): GlobalCollectionId[] {
		return (entitlements.value ?? [])
			.filter((entitlement) => entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name }))
			.map((entitlement) => decodeEntitlementKey(entitlement).resourceId)
			.filter((resourceId): resourceId is GlobalCollectionId => resourceId !== undefined);
	}

	function addResourceSelection(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId: GlobalCollectionId) {
		const key = encodeEntitlementKey({ resource: resource.name, action: action.name, resourceId });

		if (entitlements.value?.includes(key)) {
			return;
		}

		entitlements.value = [...entitlements.value ?? [], key];
	}

	function filterEntitlements(resource: MonospacePolicyEntitlementResource, action: PolicyEntitlementAction, resourceId?: GlobalCollectionId) {
		const values = entitlements.value ?? [];

		if (resourceId) {
			return removeEntitlementKey(values, { resource: resource.name, action: action.name, resourceId });
		}

		return values.filter((entitlement) => !entitlementKeyMatchesAction(entitlement, { resource: resource.name, action: action.name }));
	}
}
